/**
 * "Checks on arrival": what a stream checks as rows arrive, in one card.
 *
 * A summary, never a place to edit. Each check lives where it always has
 * and the card links there: duplicates on the stream's Settings tab,
 * suppression lists and phone checks (HLR) on each route, on the Delivery
 * tab. The server reads the stored settings the way the gates do
 * (`ArrivalChecks` on the campaign detail), so this cannot disagree with
 * what the engine applies.
 *
 * These checks assume a row is about a person, with an email or a phone. A
 * stream of orders, bookings or "other" says so; a new one starts with the
 * duplicate check off, and an existing one keeps whatever it has.
 */
import { Anchor, Badge, Card, Group, Stack, Text } from '@mantine/core';
import type { components } from '../../api/schema';
import { useVocab, type Vocab } from '../../vocab';
import { TOKENS } from '../../theme';

export type ArrivalChecksData = components['schemas']['ArrivalChecks'];

/** Where a check is edited: a tab of the stream's own page. */
export type CheckTab = 'settings' | 'delivery';

type Line = {
  key: string;
  label: string;
  on: boolean;
  /** The short chip at the top: "Duplicates, 30 days". */
  chip: string;
  detail: string;
  tab: CheckTab;
  tabLabel: string;
};

function days(n: number): string {
  return n === 1 ? '1 day' : `${n} days`;
}

function of(n: number, total: number, v: Vocab): string {
  return total === 1 ? `its ${v.one('route')}` : `${n} of ${total} ${v.many('route')}`;
}

/** Each check, in words, in the order a row meets them. */
export function arrivalLines(c: ArrivalChecksData, v: Vocab): Line[] {
  const anyDedupe = c.dedupe_api || c.dedupe_import;
  const span = c.dedupe_lookback_days > 0 ? `the last ${days(c.dedupe_lookback_days)}` : 'any time';
  const where = c.dedupe_api && c.dedupe_import
    ? `${v.many('lead')} posted to the API and imported`
    : c.dedupe_api ? `${v.many('lead')} posted to the API` : `imported ${v.many('lead')}`;
  const ownLists = c.suppression_lists > 0;
  const routeLists = c.routes_with_suppression > 0;
  return [
    {
      key: 'duplicates',
      label: 'Duplicates',
      on: anyDedupe,
      chip: anyDedupe
        ? (c.dedupe_lookback_days > 0 ? `Duplicates, ${days(c.dedupe_lookback_days)}` : 'Duplicates')
        : 'Duplicates off',
      detail: anyDedupe
        ? `Refused when the same email arrived in ${span}, on ${where}.${c.dedupe_set ? '' : ' On because nothing is set: the API checks by default.'}`
        : `Off. The same email can arrive more than once.`,
      tab: 'settings',
      tabLabel: 'Settings',
    },
    {
      key: 'suppression',
      label: 'Suppression lists',
      on: ownLists || routeLists,
      chip: 'Suppression',
      detail: ownLists || routeLists
        ? [
            ownLists ? `${c.suppression_lists === 1 ? '1 list' : `${c.suppression_lists} lists`} on this ${v.one('campaign')}` : '',
            routeLists ? `lists on ${of(c.routes_with_suppression, c.routes, v)}` : '',
          ].filter(Boolean).join(', and ').replace(/^./, (x) => x.toUpperCase())
          + (c.suppression_on_error === 'deliver' ? '. Sent unchecked if a list cannot be read.' : '. Held back if a list cannot be read.')
        : 'None.',
      tab: 'delivery',
      tabLabel: 'Delivery',
    },
    {
      key: 'phone',
      label: 'Phone check (HLR)',
      on: c.routes_with_phone_check > 0,
      chip: 'Phone check',
      detail: c.routes_with_phone_check > 0
        ? `Checked with the networks before sending, on ${of(c.routes_with_phone_check, c.routes, v)}.`
        : `Off on every ${v.one('route')}.`,
      tab: 'delivery',
      tabLabel: 'Delivery',
    },
  ];
}

export function ArrivalChecksCard({
  checks, onOpen,
}: { checks: ArrivalChecksData; onOpen: (tab: CheckTab) => void }) {
  const v = useVocab();
  const lines = arrivalLines(checks, v);
  const on = lines.filter((l) => l.on);
  return (
    <Card withBorder padding="sm" data-testid="arrival-checks">
      <Stack gap={8}>
        <Group justify="space-between" align="center" wrap="wrap" gap="xs">
          <Text size="sm" fw={600}>Checks on arrival</Text>
          <Group gap={6} wrap="wrap" data-testid="arrival-chips">
            {on.length === 0 && (
              <Badge size="sm" variant="light" color="gray" radius="xl" tt="none">None</Badge>
            )}
            {on.map((l) => (
              <Badge key={l.key} size="sm" variant="light" color="chartGreen" radius="xl" tt="none">{l.chip}</Badge>
            ))}
          </Group>
        </Group>
        {!checks.about_a_person && (
          <Text size="xs" c="dimmed" data-testid="arrival-not-a-person">
            {`These ${v.many('lead')} are not about a person, so checks on an email or a phone usually do not apply. A new ${v.one('campaign')} like this starts with them off; this one keeps what it has until you change it.`}
          </Text>
        )}
        <Stack gap={6}>
          {lines.map((l) => (
            <Group key={l.key} justify="space-between" align="flex-start" wrap="nowrap" gap="sm" data-testid={`arrival-${l.key}`}>
              <div style={{ minWidth: 0 }}>
                <Group gap={6} wrap="nowrap">
                  <Text size="sm" fw={500}>{l.label}</Text>
                  <Badge size="xs" variant="outline" color={l.on ? 'chartGreen' : 'gray'} tt="none">{l.on ? 'On' : 'Off'}</Badge>
                </Group>
                <Text size="xs" c="dimmed">{l.detail}</Text>
              </div>
              <Anchor
                component="button"
                type="button"
                size="xs"
                // The theme's link grey is under 4.5:1 on a dark card; the
                // text colour, underlined, reads as a link in both schemes.
                c={TOKENS.text}
                underline="always"
                onClick={() => onOpen(l.tab)}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {`Edit in ${l.tabLabel}`}
              </Anchor>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}
